import { useState } from 'react';
import { Box, Typography } from '@mui/material';
import { martTicketBottomPerforationSrc, xpCoinSrc } from '../../assets/bazzar';
import MartDetailsDialog from './MartDetailsDialog';
import MartInsufficientXpDialog from './MartInsufficientXpDialog';

const TICKET_WIDTH = 390;
const REDEEM_BUTTON_HEIGHT = 40;

const headerFallbackGradient = {
  cinema: 'linear-gradient(145deg, #0b0205, #2e1500 45%, #0b0205)',
  food: 'linear-gradient(145deg, #4f1300, #d83a00 45%, #f0b135)',
  fashion: 'linear-gradient(145deg, #123d58, #ffffff 48%, #e6006e)',
};

const bannerBottomMask = {
  WebkitMaskImage:
    'radial-gradient(circle 12px at bottom left, transparent 98%, black 100%), radial-gradient(circle 12px at bottom right, transparent 98%, black 100%)',
  maskImage:
    'radial-gradient(circle 12px at bottom left, transparent 98%, black 100%), radial-gradient(circle 12px at bottom right, transparent 98%, black 100%)',
  WebkitMaskComposite: 'source-in',
  maskComposite: 'intersect',
};

const contentTopMask = {
  WebkitMaskImage:
    'radial-gradient(circle 12px at top left, transparent 98%, black 100%), radial-gradient(circle 12px at top right, transparent 98%, black 100%)',
  maskImage:
    'radial-gradient(circle 12px at top left, transparent 98%, black 100%), radial-gradient(circle 12px at top right, transparent 98%, black 100%)',
  WebkitMaskComposite: 'source-in',
  maskComposite: 'intersect',
};

const contentPanelGradient =
  'linear-gradient(180deg, #940cdb 0%, #8708e7 25%, #7804f2 50%, #6b00fe 75%, #6a00ff 100%)';

function parseProgress(value = '0%') {
  const match = String(value).match(/(\d+)/);
  const amount = match ? Number(match[1]) : 0;
  return Math.min(Math.max(amount, 0), 100);
}

export default function MartCard({ card, onClick, userXp = 0 }) {
  const [detailsOpen, setDetailsOpen] = useState(false);
  const [insufficientXpOpen, setInsufficientXpOpen] = useState(false);
  const progress = parseProgress(card.progress);
  const requiredXp = Number(card.xpValue) || 0;
  const canRedeem = requiredXp > 0 && userXp >= requiredXp;
  const headerImage = card.imageUrl;
  const headerGradient = headerFallbackGradient[card.imageTone] || headerFallbackGradient.fashion;

  const handleRedeem = (event) => {
    event.stopPropagation();
    if (!canRedeem) {
      setInsufficientXpOpen(true);
      return;
    }
    setDetailsOpen(false);
    onClick(card);
  };

  const handleOpenDetails = () => {
    setDetailsOpen(true);
  };

  const handleDetailsRedeem = (selectedCard) => {
    setDetailsOpen(false);
    onClick(selectedCard);
  };

  return (
    <>
    <Box
      onClick={handleOpenDetails}
      sx={{
        width: '250px',
        maxWidth: { xs: TICKET_WIDTH, md: TICKET_WIDTH },
        borderRadius: '16px 16px 0 0',
        overflow: 'visible',
        boxShadow: '0 20px 45px rgba(18, 0, 48, 0.5)',
        bgcolor: 'transparent',
        background: 'none',
        cursor: 'pointer',
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      <Box
        sx={{
          height: { xs: 165, md: 180 },
          overflow: 'hidden',
          flexShrink: 0,
          borderRadius: '15px 15px 0 0',
          border: '1px solid rgba(255, 255, 255, 0.95)',
          borderBottom: 0,
          position: 'relative',
          ...bannerBottomMask,
          '&::before, &::after': {
            content: '""',
            position: 'absolute',
            bottom: { xs: -12, md: -12 },
            width: 29,
            height: 25,
            borderRadius: '50%',
            background: 'transparent',
            zIndex: 3,
          },
          '&::before': {
            left: -17,
            borderRight: '1px solid rgba(255, 255, 255, 0.95)',
          },
          '&::after': {
            right: -17,
            borderLeft: '1px solid rgba(255, 255, 255, 0.95)',
          },
        }}
      >
        {headerImage ? (
          <Box
            component="img"
            src={headerImage}
            alt={card.brand || 'Voucher'}
            sx={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
          />
        ) : (
          <Box
            sx={{
              width: '100%',
              height: '100%',
              background: headerGradient,
              display: 'grid',
              placeItems: 'center',
              color: card.brand === 'PVR' ? '#ffc400' : '#fff',
              fontSize: { xs: 18, md: 22 },
              fontWeight: 900,
            }}
          >
            {card.brand}
          </Box>
        )}
      </Box>

      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          flexShrink: 0,
          position: 'relative',
          borderLeft: '1px solid rgba(255, 255, 255, 0.95)',
          borderRight: '1px solid rgba(255, 255, 255, 0.95)',
          borderTop: '1px solid rgba(255, 255, 255, 0.95)',
          borderBottom: 0,
          ...contentTopMask,
          '&::before, &::after': {
            content: '""',
            position: 'absolute',
            top: { xs: -12, md: -12 },
            width: 29,
            height: 25,
            borderRadius: '50%',
            background: 'transparent',
            zIndex: 3,
          },
          '&::before': {
            left: -17,
            borderRight: '1px solid rgba(255, 255, 255, 0.95)',
          },
          '&::after': {
            right: -17,
            borderLeft: '1px solid rgba(255, 255, 255, 0.95)',
          },
        }}
      >
        <Box
          sx={{
            position: 'relative',
            px: { xs: '21px', md: '21px' },
            pt: { xs: '24px', md: '24px' },
            pb: { xs: '31px', md: '31px' },
            textAlign: 'center',
            color: '#fff',
            background: contentPanelGradient,
            ...contentTopMask,
          }}
        >
          <Typography
            sx={{
              fontWeight: 700,
              fontSize: { xs: 16, md: 16 },
              lineHeight: 1.2,
              mb: '14px',
              letterSpacing: '0.9px',
            }}
          >
            {card.title}
          </Typography>

          <Box
            sx={{
              height: 14,
              p: '2px',
              borderRadius: '999px',
              bgcolor: '#d4d4d4',
              boxShadow: 'inset 0 1px 2px rgba(0, 0, 0, 0.35)',
              overflow: 'hidden',
              mb: '20px',
            }}
          >
            <Box
              sx={{
                width: `${progress}%`,
                height: '100%',
                borderRadius: 'inherit',
                background: 'linear-gradient(90deg, #18df08, #03b517)',
              }}
            />
          </Box>

          <Box
            component="button"
            type="button"
            onClick={handleRedeem}
            sx={{
              position: 'relative',
              width: '100%',
              maxWidth: '100%',
              height: REDEEM_BUTTON_HEIGHT,
              mb: 0,
              border: canRedeem ? '1px solid #ffc5f7' : 0,
              borderRadius: '999px',
              p: 0,
              cursor: canRedeem ? 'pointer' : 'not-allowed',
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '6px',
              color: '#fff',
              background: canRedeem
                ? 'linear-gradient(270deg, #ff00f2 0%, #822aff 100%)'
                : 'linear-gradient(90deg, #6b6b6b 0%, #8a8a8a 100%)',
              boxShadow: canRedeem
                ? 'inset 2px 2px 5px rgba(255, 255, 255, 0.35), inset -2px -2px 6px rgba(0, 0, 0, 0.25), 0 4px 0 rgba(0, 0, 0, 0.64)'
                : 'none',
              opacity: canRedeem ? 1 : 0.72,
              fontSize: 15,
              letterSpacing: '0.2px',
            }}
          >
            <Box
              sx={{
                width: 17,
                height: 17,
                borderRadius: '100px',
                overflow: 'hidden',
                flexShrink: 0,
                position: 'relative',
              }}
            >
              <Box
                component="img"
                src={xpCoinSrc}
                alt=""
                sx={{
                  position: 'absolute',
                  width: '178.09%',
                  height: '118.72%',
                  maxWidth: 'none',
                  left: '-39.86%',
                  top: '-8.2%',
                }}
              />
            </Box>
            <Typography
              component="span"
              sx={{
                fontWeight: 500,
                fontSize: 10,
                lineHeight: 1,
                letterSpacing: 0,
                textTransform: 'uppercase',
               
              }}
            >
              {card.cost} - Redeem
            </Typography>
          </Box>
        </Box>
          <Box
            component="img"
            src={martTicketBottomPerforationSrc}
            alt=""
            aria-hidden="true"
            sx={{
              width: '100%',
              height: 4,
              display: 'block',
              
            }}
          />
      </Box>
    </Box>

    <MartDetailsDialog
      open={detailsOpen}
      card={card}
      userXp={userXp}
      onClose={() => setDetailsOpen(false)}
      onRedeem={handleDetailsRedeem}
      onInsufficientXp={() => setInsufficientXpOpen(true)}
    />

    <MartInsufficientXpDialog
      open={insufficientXpOpen}
      onClose={() => setInsufficientXpOpen(false)}
    />
    </>
  );
}
